import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const AchievementBadges = () => {
  const achievements = [
    {
      icon: "Award",
      title: "Excellence in IT Engineering",
      description: "Recognized for outstanding technical contributions",
      year: "2025",
      color: "var(--color-primary)"
    },
    {
      icon: "Trophy",
      title: "Entrepreneur of the Year",
      description: "African Tech Innovation Awards",
      year: "2024",
      color: "var(--color-accent)"
    },
    {
      icon: "Star",
      title: "Top Mentor Recognition",
      description: "Impacted 100+ young professionals",
      year: "2024",
      color: "var(--color-secondary)"
    },
    {
      icon: "Zap",
      title: "Innovation Leader",
      description: "Digital transformation excellence",
      year: "2023",
      color: "var(--color-success)"
    },
    {
      icon: "Target",
      title: "Business Excellence",
      description: "Multiple successful venture launches",
      year: "2023",
      color: "var(--color-primary)"
    },
    {
      icon: "Sparkles",
      title: "Creative Excellence",
      description: "Ed Pulse brand recognition",
      year: "2024",
      color: "var(--color-accent)"
    }
  ];

  const certifications = [
    { name: "AWS Certified Solutions Architect", issuer: "Amazon Web Services", year: "2024" },
    { name: "Google Cloud Professional", issuer: "Google Cloud", year: "2023" },
    { name: "React Advanced Certification", issuer: "Meta", year: "2023" },
    { name: "Business Strategy Certificate", issuer: "Harvard Business School", year: "2022" },
    { name: "Agile Project Management", issuer: "PMI", year: "2022" },
    { name: "UI/UX Design Professional", issuer: "Nielsen Norman Group", year: "2021" }
  ];

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 lg:mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-4">
            <Icon name="Award" size={18} color="var(--color-primary)" />
            <span className="text-sm font-medium text-primary">Recognition</span>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4">Achievements & Certifications</h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
            Professional recognition and industry certifications validating expertise
          </p>
        </motion.div>

        <div className="mb-12 lg:mb-16">
          <h3 className="text-xl md:text-2xl font-semibold mb-6 lg:mb-8">Awards & Recognition</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {achievements?.map((achievement, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                whileHover={{ y: -8, transition: { duration: 0.3 } }}
                className="bg-card rounded-xl lg:rounded-2xl p-6 lg:p-8 border border-border hover:border-primary/50 transition-colors text-center"
              >
                <motion.div
                  animate={{ 
                    rotate: [0, 10, -10, 0],
                    scale: [1, 1.1, 1]
                  }}
                  transition={{ 
                    duration: 2,
                    repeat: Infinity,
                    repeatDelay: 3
                  }}
                  className="w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: `${achievement?.color}15` }}
                >
                  <Icon name={achievement?.icon} size={32} color={achievement?.color} />
                </motion.div>
                <h4 className="text-base md:text-lg font-semibold mb-2">{achievement?.title}</h4>
                <p className="text-sm text-muted-foreground mb-3">{achievement?.description}</p>
                <span className="inline-block px-3 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full">
                  {achievement?.year}
                </span>
              </motion.div>
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-xl md:text-2xl font-semibold mb-6 lg:mb-8">Professional Certifications</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {certifications?.map((cert, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05, duration: 0.4 }}
                className="bg-card rounded-xl p-4 lg:p-6 border border-border hover:border-primary/50 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon name="CheckCircle2" size={20} color="var(--color-primary)" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm md:text-base font-semibold mb-1">{cert?.name}</h4>
                    <p className="text-xs md:text-sm text-muted-foreground mb-1">{cert?.issuer}</p>
                    <span className="text-xs text-primary font-medium">{cert?.year}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AchievementBadges;